import React from 'react';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { Trans, useLingui } from '@lingui/react';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { Stack } from '@startuptools/ui/Stack';
import { FormControl } from '@startuptools/ui/FormControl';
import { Typography } from '@startuptools/ui/Typography';
import { Alert } from '@startuptools/ui/Alert';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { type GqlWarrantParticipant } from '../../../../../../graphql/base-types.graphql';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { BigNumber } from 'bignumber.js';
import { FormHelperText } from '@startuptools/ui/joy/FormHelperText';

type FormData = { quantityRepurchased: string };

export const Repurchase = ({
  error,
  closeDialog,
  onSubmit,
  participant,
  loading,
}: {
  error?: React.ReactNode | undefined;
  closeDialog: () => void;
  onSubmit: SubmitHandler<FormData>;
  loading: boolean;
  participant: Pick<GqlWarrantParticipant, 'id' | 'quantity' | 'quantityRepurchased'>;
}) => {
  const form = useForm<FormData>({
    defaultValues: {
      quantityRepurchased: participant.quantityRepurchased ?? '',
    },
  });

  const { i18n } = useLingui();

  return (
    <>
      <Typography>
        <Trans id="Repurchase options" />
      </Typography>
      <form
        onSubmit={form.handleSubmit(async data => {
          await onSubmit(data);
          closeDialog();
        })}
      >
        <Box sx={{ width: '420px', overflowY: 'auto' }}>
          <Stack spacing={3}>
            <Controller
              control={form.control}
              name="quantityRepurchased"
              rules={{
                validate: value => {
                  if (new BigNumber(participant.quantity ?? '0').minus(value).lt(0)) {
                    return 'greater_than_available';
                  }
                  return true;
                },
              }}
              render={({ field: { onChange, ...field } }) => (
                <FormControl error={form.formState.errors.quantityRepurchased !== undefined}>
                  <FormLabel>
                    <Trans id="Quantity repurchased" />
                  </FormLabel>
                  {form.formState.errors.quantityRepurchased && (
                    <FormHelperText>
                      {form.formState.errors.quantityRepurchased.message === 'greater_than_available' ? (
                        <Trans id="Cannot be greater than the quantity of issued shares" />
                      ) : (
                        ''
                      )}
                    </FormHelperText>
                  )}
                  <NumericFormatInput {...field} onChange={onChange} value={field.value} decimalScale={0} />
                </FormControl>
              )}
            />
            <Alert>
              <Trans
                id="The subscriber will have {quantity} options left. Options are repurchased on a pro-rata basis, proportionally reducing both vested and unvested shares."
                values={{
                  quantity: (
                    <NumberFormat locale={i18n.locale} maximumFractionDigits={0}>
                      {new BigNumber(participant.quantity ?? '0').minus(form.watch('quantityRepurchased')).lt(0)
                        ? '0'
                        : new BigNumber(participant.quantity ?? '0')
                            .minus(form.watch('quantityRepurchased'))
                            .toString()}
                    </NumberFormat>
                  ),
                }}
              />
            </Alert>
            {error}
            <Stack direction="row" gap={1} justifyContent="end" alignItems="center">
              <Button variant="plain" onClick={closeDialog} color="neutral">
                <Trans id="Cancel" />
              </Button>
              <Button type="submit" variant="solid" color="primary" loading={loading}>
                <Trans id="Save" />
              </Button>
            </Stack>
          </Stack>
        </Box>
      </form>
    </>
  );
};
